import { firstQuestionId, type Answers, type QuizDefinition } from "@/schema/quizSchema";

export type QuizState =
  | { phase: "running"; currentQuestionId: string; answers: Answers; step: number }
  | { phase: "building"; answers: Answers; persona: string }
  | { phase: "result"; persona: string; answers: Answers }
  | { phase: "error"; message: string; answers: Answers; previousPhase: QuizState["phase"] };

export type QuizAction =
  | { type: "answer"; answers: Answers; nextQuestionId: string }
  | { type: "complete-questions"; answers: Answers; persona: string }
  | { type: "completed" }
  | { type: "error"; message: string }
  | { type: "retry"; firstQuestionId: string };

export function initialState(quiz: QuizDefinition): QuizState {
  return { phase: "running", currentQuestionId: firstQuestionId(quiz), answers: {}, step: 1 };
}

export function quizReducer(state: QuizState, action: QuizAction): QuizState {
  if (action.type === "error") {
    return { phase: "error", message: action.message, answers: state.answers, previousPhase: state.phase };
  }
  if (action.type === "retry") {
    return { phase: "running", currentQuestionId: action.firstQuestionId, answers: {}, step: 1 };
  }

  switch (state.phase) {
    case "running":
      if (action.type === "answer") {
        // A staged question (multi/slider/text/number) dispatches "answer" with
        // nextQuestionId === currentQuestionId just to record a draft — that's
        // not a screen advance, so the step counter (which drives the progress
        // bar's numerator) must not move. Only a real advance to a different
        // question increments it.
        const advanced = action.nextQuestionId !== state.currentQuestionId;
        return {
          phase: "running",
          currentQuestionId: action.nextQuestionId,
          answers: action.answers,
          step: advanced ? state.step + 1 : state.step,
        };
      }
      if (action.type === "complete-questions") {
        return { phase: "building", answers: action.answers, persona: action.persona };
      }
      return state;
    case "building":
      if (action.type === "completed") {
        return { phase: "result", persona: state.persona, answers: state.answers };
      }
      return state;
    case "result":
    case "error":
      return state;
  }
}
